import { Component, DoCheck, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { DownloadChartType, paths } from '@app/constants';
import { State } from '@app/core/features';
import { AuthService, selectors as authSelectors, UserData } from '@app/core/features/auth';
import { FetchLoggedInUser } from '@app/core/features/auth/auth.actions';
import { CardModel } from '@app/core/features/charts-manager/my-charts/my-charts-list';
import { MyChartsService } from '@app/core/features/charts-manager/my-charts/my-charts.service';
import { TransferChartsService } from '@app/core/features/charts-manager/my-charts/transfer-charts/transfer-charts.services';
import { PaymentService } from '@app/core/features/payment/payment.service';
import { LanguageService } from '@app/core/services/language/language.service';
import { Globals } from '@app/shared/models';
import { isMobileView } from '@app/utils';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { selectors as maintenanceModesSelector, MaintenanceMode } from '@app/core/features/maintenance-mode';
import { Location } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@app/shared/components/base/base/base.component';

@Component({
  selector: 'app-pages',
  templateUrl: './pages.component.html',
  styleUrls: ['./pages.component.scss'],
})
export class PagesComponent extends BaseComponent implements OnInit, DoCheck {
  isActive: boolean;
  isChartManager: boolean;
  isPreparingPopoverShow = false;
  isReadyPopoverShow = false;
  chartPreparing$ = new BehaviorSubject<CardModel[]>([]);
  chartReady$ = new BehaviorSubject<CardModel[]>([]);
  transferSuccess$ = new BehaviorSubject<boolean>(false);
  isMobileView: boolean;
  isLoadHeader = false;
  isPaymentCheckoutPage: boolean = false;
  loggedInUser: Observable<UserData>;
  hasError: Observable<boolean>;
  paymentSuccess$: boolean;
  maintenanceServerMode$: Observable<MaintenanceMode>;
  maintenancePaymentMode$: Observable<MaintenanceMode>;

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private router: Router,
    public globals: Globals,
    private myChartsService: MyChartsService,
    protected store: Store<State>,
    private languageService: LanguageService,
    private authService: AuthService,
    public transferChartService: TransferChartsService,
    private paymentService: PaymentService,
    private location: Location
  ) {
    super();
    router.events.subscribe(event => {
      this.isChartManager = this.router.url.substr(1).startsWith(paths.CHARTS_MANAGER);
      this.isPaymentCheckoutPage = this.router.url.substr(1).startsWith(`${paths.PAYMENT}/${paths.CHECKOUT}`);

      if (event instanceof NavigationEnd) {
        this.languageService.findMetaData(event.urlAfterRedirects);
      }
    });

    this.svgIconRegister();

    this.paymentService.getPaymentSuccessStatus().subscribe(status => {
      this.paymentSuccess$ = status;
    });
  }

  private svgIconRegister = (): void => {
    this.matIconRegistry.addSvgIcon(
      'iconChart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_charts.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconChartDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_charts_disabled.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconLightHouse',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_lighthouse_charts.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconLightHouseDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_lighthouse_charts_disabled.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownload',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_download_large.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownloadReady',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_download_ready.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownloadDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_download_large_grey.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconPoi',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_poi.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAerial',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_aerial.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconGrib',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_grib.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconGribColor',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icons_grib-color.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconPoiDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_poi_disabled.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAerialDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_aerial_disabled.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconGribDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_grib_disabled.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconLncChart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_lnc_charts.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconLncChartDisabled',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_lnc_charts_disabled.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconQualified',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/QUALIFIED.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_cart.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconTransferOwnership',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_transfer_ownership.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAddToCart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/iconAddToCart.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconBack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_back.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconChartByRegion',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_charts_by_region.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconChartFromList',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_charts_from_list.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconSearch',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_search.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconLanguage',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svgs/icon_language.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'btnReset',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/reset_button.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconUndo',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/iconUndo.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconMiniVoucher',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icons_mini-voucher.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCheck',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_check.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconUnCheck',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_uncheck.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'icon90Days',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_90days.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAddAll',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_add_all.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconClearAll',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/clear-all.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDefineArea',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_define_area.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDrag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_drag.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAddAnother',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_add_another.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconExceedSizeLimit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_exceed_size_limit.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'smallIconExceedSizeLimit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/small_icon_exceed_size_limit.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownloadOptionsCollapse',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_download_options_collapse.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconMobileDownload',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_mobile_download.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconNotSelect',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_not_select.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconSelect',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_select.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconMenuHeader',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svgs/icon_menu_header.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconMenuHeaderClose',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_menu_header_close.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconHeaderLogo',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svgs/icon_header_logo.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconFilterDown',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_filter_down.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconFilterUp',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_filter_up.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconHeaderChartStore',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svgs/icon_header_chart_store.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconHeaderMyChart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svgs/icon_header_my_chart.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconWarning',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_warning.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconRaymarine',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_raymarine.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconRaymarineBlack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_raymarine_black.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconClose',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_close.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconSDCard',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_sd_card.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCorrect',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_correct.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconIncorrect',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_incorrect.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAppleStore',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_apple_store.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconGooglePlay',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_google_play.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconLine',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_line.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconClosePopup',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_close_popup.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconNext',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_next.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDone',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_done.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconHowto',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_howto.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconUpdate',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_update.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownloadBack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_download_back.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconBlueDownloadBack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_blue_download_back.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconDownloadNext',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_download_next.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconBlueDownloadNext',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_blue_download_next.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconButtonAddToCart',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_add_to_cart.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconOption',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_option.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconImprove',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_improve.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCalendar',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_calendar.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconSupport',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svgs/icon_support.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCheckTickGreen',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_check_tick_green.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCheckTickWhite',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_check_tick_white.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconRaymarineCloud',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_raymarine_cloud.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconRaymarineStraightDots',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_raymarine_straight_dots.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconRaymarinePC',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_raymarine_pc.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconRaymarineMicroSD',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_raymarine_microSD.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconArrows',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_arrows.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconInputVoucher',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_input_voucher.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconNonRaymarineMicroSD',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_non-raymarine-card_microSD.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconBlackVoucher',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_black_voucher.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconPC',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_PC.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconMicroSD',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_microSD_card.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCellphoneFront',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_cellphone_front.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCellphoneBehind',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_cellphone_behind.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconBlankCard',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_blank_card.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCardA',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_card_A.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconArrowsDetail',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_arrows_detail.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconNotes',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_notes.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconPencil',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_pencil.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAddName',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_add_name.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'auFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/au.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'caFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/ca.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'ca-frFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/ca.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'deFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/de_new.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'dkFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/dk.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'esFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/es.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'euFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/eu.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'fiFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/fi.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'frFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/fr.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'itFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/it.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'nlFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/nl.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'noFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/no.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'seFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/se.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'ukFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/uk.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'usFlag',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/nation-flag/us.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconBlackBack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_black_back.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconHeaderHowToGuide',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_header_how_to_guide.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconInformation',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_information.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAxiom',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/Axiom_vector.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconElement',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/Element_vector.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconCloseDialog',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon-close-dialog.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconEdit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_edit.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAddNewAddress',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_add_new_address.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconChevronRightBlack',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_chevron_right_black.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconMySubscriptions',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_my_subscriptions.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconPersonalInfo',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_personal_info.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconVerticalLine',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_vertical_line.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconAlert',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_alert.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconNotification',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_notification.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'iconEstimatedFileSize',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/icon_estimated_file_size.svg')
    );
  }

  ngOnInit() {
    this.isMobileView = isMobileView();
    this.languageService.findMetaData();
    this.isActive = this.globals.isActive;

    this.chartPreparing$ = this.myChartsService.chartPreparing$;
    this.chartReady$ = this.myChartsService.chartReady$;
    this.transferSuccess$ = this.transferChartService.transferSuccess$;

    if (this.authService.checkoutAccount()) {
      this.store.dispatch(new FetchLoggedInUser());

      this.loggedInUser = this.store.pipe(authSelectors.getUserData);
      this.hasError = this.store.pipe(authSelectors.getHasError);
    } else {
      this.isLoadHeader = true;
    }

    this.maintenanceServerMode$ = this.store
    .pipe(maintenanceModesSelector.getServerModeData)
    .pipe(takeUntil(this.ngUnsubscribe));

    this.maintenancePaymentMode$ = this.store
    .pipe(maintenanceModesSelector.getPaymentModeData)
    .pipe(takeUntil(this.ngUnsubscribe));
  }

  ngGetPageClass(): string {
    // Router URL has structure {pageRouteId}/{subpageRouteId}{?queryParams}
    const pageRouteId = this.router.url.split("/")[1];
    const subpageRouteId = this.router.url.split("/")[2];

    // Default class name
    let defaultClassName = 'content';
    let pageClassName = ''
    let subpageClassName = ''

    for (const key in paths) {
      if (Object.prototype.hasOwnProperty.call(paths, key)) {
        const path = paths[key];

        if (path === subpageRouteId) {
          subpageClassName = ` page-${pageRouteId}-${subpageRouteId}`;
        }

        if (path === pageRouteId) {
          pageClassName = ` page-${pageRouteId}`;
        }
      }
    }

    if (subpageClassName !== '') {
      return defaultClassName + subpageClassName;
    } else if (pageClassName !== '') {
      return defaultClassName + pageClassName;
    }

    return defaultClassName;
  }

  ngDoCheck() {
    this.isActive = this.globals.isActive;
  }

  redirectToDownloadPage(event, lighthouseId: string) {
    event.preventDefault();

    this.isPreparingPopoverShow = false;
    this.isReadyPopoverShow = false;

    return this.router.navigate(
      [paths.CHARTS_MANAGER, paths.DOWNLOAD_CHART, lighthouseId],
      { queryParams: { type: DownloadChartType.HasLightHouseId, checked: true } }
    );
  }

  clearTransferMessage() {
    this.transferChartService.transferSuccess$.next(false);
  }

  backToPreviousPage() {
    this.location.back();
  }

  isPaymentPage() {
    return this.router.url.includes(paths.PAYMENT);
  }
}
